import { useFragment } from "@/generated";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { MoveProjectsDocument, ProjectFieldsFragmentDoc } from "../../../generated/graphql";

/**
 * Hook for moving projects to a different parent project or to root level.
 *
 * Provides a mutation function to relocate one or more projects to a new parent project
 * or to the root level (no parent). Automatically refetches root and parent project
 * queries after the move to update the UI with the new project locations.
 *
 * @returns An object containing:
 *   - moveProjects: Async function to move projects. Takes projectIds array and targetParentId (or null for root).
 *   - loading: Boolean indicating if the mutation is in progress
 *
 * @example
 * const { moveProjects, loading } = useMoveProjects();
 * await moveProjects(["project1", "project2"], "parentProjectId"); // Move to parent
 * await moveProjects(["project1"], null); // Move to root
 */
export function useMoveProjects() {
  const [moveProjectsMutation, { loading }] = useMutation(MoveProjectsDocument, {
    update(cache, { data }, { context, variables }) {
      if (!data?.moveProject) return;

      try {
        const projects = data.moveProject.map((project) =>
          useFragment(ProjectFieldsFragmentDoc, project)
        );

        const targetParentId = variables?.input.targetParentId;
        const sourceParentId = context?.sourceParentId;

        // Allow null (root level) as a valid sourceParentId
        if (sourceParentId === undefined) return;

        cache.batch({
          // Add project to target project cache
          update(cache) {
            for (const project of projects) {
              const id = cache.identify(project);
              if (id) {
                cache.writeFragment({
                  id,
                  fragment: ProjectFieldsFragmentDoc,
                  data: project,
                });
              }
            }

            const newRefs = projects
              .map((p) => cache.identify(p))
              .filter(Boolean)
              .map((id) => ({ __ref: id }));

            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                projectsByParentId(existingData, { storeFieldName }) {
                  const key =
                    targetParentId === null ? `"parentId":null` : `"parentId":"${targetParentId}"`;
                  const isTrashKey = '"isTrash":false';

                  if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                    return existingData;
                  if (!existingData?.items) return existingData;

                  return {
                    ...existingData,
                    items: [...newRefs, ...existingData.items],
                    total: (existingData.total || 0) + newRefs.length,
                  };
                },
              },
            });

            if (targetParentId !== null) {
              cache.modify({
                id: `Project:${targetParentId}`,
                fields: {
                  itemCount(existingData = 0) {
                    return existingData + newRefs.length;
                  },
                },
              });
            }

            // Remove project from source project cache
            cache.modify({
              id: "ROOT_QUERY",
              fields: {
                projectsByParentId(existingData, { storeFieldName, readField }) {
                  const key =
                    sourceParentId === null ? `"parentId":null` : `"parentId":"${sourceParentId}"`;
                  const isTrashKey = '"isTrash":false';

                  if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                    return existingData;
                  if (!existingData?.items) return existingData;

                  return {
                    ...existingData,
                    items: existingData.items.filter(
                      (ref: Reference) => !projects.some((p) => readField("id", ref) === p.id)
                    ),
                    total: (existingData.total || 0) - newRefs.length,
                  };
                },
              },
            });

            if (sourceParentId !== null) {
              cache.modify({
                id: `Project:${sourceParentId}`,
                fields: {
                  itemCount(existingData = 0) {
                    return existingData - newRefs.length;
                  },
                },
              });
            }
          },
        });
      } catch (error) {
        console.error("Move projects cache update failed:", error);
      }
    },
  });

  const moveProjects = async (
    projectIds: string[],
    targetParentId: string | null,
    sourceParentId: string | null
  ) => {
    const result = await moveProjectsMutation({
      variables: {
        input: {
          projectIds,
          targetParentId,
        },
      },
      context: {
        sourceParentId,
      },
    });

    const maskedProjects = result.data?.moveProject ?? [];
    const movedProjects = maskedProjects.map((project) =>
      useFragment(ProjectFieldsFragmentDoc, project)
    );

    return movedProjects;
  };

  return { moveProjects, loading };
}
